import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useWorkspace } from '@/hooks/useData';

const DEBOUNCE_MS = 250;
const LIMIT_PER_TYPE = 5;

const escapeIlike = (s) => String(s).replace(/[%,()]/g, ' ').trim();

export default function useGlobalSearch(rawQuery) {
  const { workspace } = useWorkspace();
  const workspaceId = workspace?.id;

  const [results, setResults] = useState({ invoices: [], clients: [], expenses: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reqIdRef = useRef(0);

  useEffect(() => {
    const q = escapeIlike(rawQuery || '');
    if (!workspaceId || q.length < 2) {
      setResults({ invoices: [], clients: [], expenses: [] });
      setLoading(false);
      setError(null);
      return undefined;
    }

    const myReq = ++reqIdRef.current;
    setLoading(true);
    setError(null);

    const timer = setTimeout(async () => {
      const pattern = `%${q}%`;
      try {
        const [invRes, cliRes, expRes] = await Promise.all([
          supabase
            .from('invoices')
            .select('id, number, variable_symbol, status, total, due_date, issue_date, client_id')
            .eq('workspace_id', workspaceId)
            .or(`number.ilike.${pattern},variable_symbol.ilike.${pattern}`)
            .order('issue_date', { ascending: false })
            .limit(LIMIT_PER_TYPE),
          supabase
            .from('clients')
            .select('id, name, ico, dic, email')
            .eq('workspace_id', workspaceId)
            .or(`name.ilike.${pattern},ico.ilike.${pattern},dic.ilike.${pattern},email.ilike.${pattern}`)
            .order('is_pinned', { ascending: false })
            .order('name', { ascending: true })
            .limit(LIMIT_PER_TYPE),
          supabase
            .from('expenses')
            .select('id, vendor_name, vendor_ico, description, amount, date, category')
            .eq('workspace_id', workspaceId)
            .or(`vendor_name.ilike.${pattern},vendor_ico.ilike.${pattern},description.ilike.${pattern}`)
            .order('date', { ascending: false })
            .limit(LIMIT_PER_TYPE),
        ]);

        if (myReq !== reqIdRef.current) return;

        const firstErr = invRes.error || cliRes.error || expRes.error;
        if (firstErr) {
          setError(firstErr.message || 'Hľadanie zlyhalo.');
          setResults({ invoices: [], clients: [], expenses: [] });
        } else {
          setResults({
            invoices: invRes.data || [],
            clients: cliRes.data || [],
            expenses: expRes.data || [],
          });
        }
      } catch (e) {
        if (myReq !== reqIdRef.current) return;
        setError(e?.message || 'Hľadanie zlyhalo.');
        setResults({ invoices: [], clients: [], expenses: [] });
      } finally {
        if (myReq === reqIdRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [rawQuery, workspaceId]);

  return { ...results, loading, error };
}
